import { useState, useEffect, useCallback } from 'react';

export const useApps = (supabase) => {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchApps = useCallback(async () => {
    try {
      setLoading(true);
      const { data: { user }, error: userError } = await supabase.auth.getUser();

      if (userError) throw userError;
      if (!user) {
        setApps([]);
        return;
      }

      // Fetch all apps for the user
      const { data: userApps, error: appsError } = await supabase
        .from('apps')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (appsError) throw appsError;

      if (!userApps || userApps.length === 0) {
        setApps([]);
        setLoading(false);
        return;
      }

      // Fetch scenario counts for each app
      const appsWithCounts = await Promise.all(
        userApps.map(async (app) => {
          const { count, error: countError } = await supabase
            .from('app_scenarios')
            .select('id', { count: 'exact' })
            .eq('app_id', app.id);

          if (countError) {
            console.error('Error counting scenarios:', countError);
            return { ...app, scenario_count: 0 };
          }

          return { ...app, scenario_count: count || 0 };
        })
      );

      setApps(appsWithCounts);
    } catch (err) {
      console.error('Error fetching apps:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  useEffect(() => {
    fetchApps();

    // Subscribe to realtime changes for apps
    const appsSubscription = supabase
      .channel('apps_changes')
      .on('postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'apps'
        },
        () => {
          fetchApps();
        }
      )
      .subscribe();

    // Subscribe to realtime changes for app_scenarios
    const appScenariosSubscription = supabase
      .channel('app_scenarios_changes')
      .on('postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'app_scenarios'
        },
        () => {
          fetchApps();
        }
      )
      .subscribe();

    return () => {
      appsSubscription.unsubscribe();
      appScenariosSubscription.unsubscribe();
    };
  }, [supabase, fetchApps]);

  const createApp = async (appData) => {
    try {
      setLoading(true);

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();

      // Add user_id to app data if not present
      if (!appData.user_id && user) {
        appData.user_id = user.id;
      }

      // Set default values if not provided
      const appToCreate = {
        theme_color: '#2563eb',
        background_color: '#ffffff',
        ...appData
      };

      const { data, error } = await supabase
        .from('apps')
        .insert([appToCreate])
        .select();

      if (error) throw error;

      await fetchApps(); // Refresh the apps list
      return data[0];
    } catch (error) {
      console.error('Error creating app:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateApp = async (id, updates) => {
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('apps')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select();

      if (error) throw error;

      await fetchApps(); // Refresh the apps list
      return data[0];
    } catch (error) {
      console.error('Error updating app:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const deleteApp = async (id) => {
    try {
      setLoading(true);

      const { error } = await supabase
        .from('apps')
        .delete()
        .eq('id', id);

      if (error) throw error;

      // Update the state to remove this app
      setApps(prevApps => prevApps.filter(app => app.id !== id));
    } catch (error) {
      console.error('Error deleting app:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const duplicateApp = async (id) => {
    try {
      setLoading(true);

      // Fetch the app to duplicate
      const { data: originalApp, error: fetchError } = await supabase
        .from('apps')
        .select('*')
        .eq('id', id)
        .single();

      if (fetchError) throw fetchError;

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();

      // Create a copy without the id and token (let DB generate new ones)
      const duplicateData = {
        name: `Copy of ${originalApp.name}`,
        user_id: user?.id || originalApp.user_id,
        description: originalApp.description,
        icon_url: originalApp.icon_url,
        logo_url: originalApp.logo_url,
        theme_color: originalApp.theme_color,
        background_color: originalApp.background_color,
        branding_text: originalApp.branding_text
      };

      // Create the new app
      const newApp = await createApp(duplicateData);

      // Fetch the scenarios linked to the original app
      const { data: linkedScenarios, error: scenariosError } = await supabase
        .from('app_scenarios')
        .select('scenario_id, display_order')
        .eq('app_id', id);

      if (scenariosError) throw scenariosError;

      // Link the same scenarios to the new app
      if (linkedScenarios && linkedScenarios.length > 0) {
        const scenarioLinks = linkedScenarios.map(link => ({
          app_id: newApp.id,
          scenario_id: link.scenario_id,
          display_order: link.display_order
        }));

        const { error: linkError } = await supabase
          .from('app_scenarios')
          .insert(scenarioLinks);

        if (linkError) throw linkError;
      }

      await fetchApps(); // Refresh to get updated counts
      return newApp;
    } catch (error) {
      console.error('Error duplicating app:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const getAppScenarios = async (appId) => {
    try {
      const { data, error } = await supabase
        .from('app_scenarios')
        .select(`
          id,
          display_order,
          scenarios (
            id,
            token,
            name,
            description
          )
        `)
        .eq('app_id', appId)
        .order('display_order', { ascending: true });

      if (error) throw error;

      // Format the data
      return (data || []).map(item => ({
        linkId: item.id,
        display_order: item.display_order,
        ...item.scenarios
      }));
    } catch (error) {
      console.error('Error fetching app scenarios:', error);
      throw error;
    }
  };

  return {
    apps,
    loading,
    error,
    refreshApps: fetchApps,
    createApp,
    updateApp,
    deleteApp,
    duplicateApp,
    getAppScenarios
  };
};
